import { motion } from "framer-motion";
import { Building2, Search, Shield, Handshake, DollarSign, ArrowRightLeft, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
const steps = [
  {
    icon: Building2,
    title: "Cadastre seu imóvel",
    description: "Publique fotos, dados e o valor estimado do seu imóvel em poucos minutos.",
  },
  {
    icon: Search,
    title: "Defina o que aceita",
    description: "Informe o tipo de imóvel, região e faixa de valor que deseja em troca.",
  },
  {
    icon: Shield,
    title: "Receba sugestões",
    description: "Nossa IA analisa milhares de imóveis e encontra matches com score de compatibilidade. Tudo anônimo até o interesse mútuo.",
  },
  {
    icon: Handshake,
    title: "Feche a permuta",
    description: "Após o match, converse no chat e libere contato quando quiser negociar.",
  },
];

const HowItWorks = () => {
  return (
    <section id="como-funciona" className="py-20 bg-background">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-3">
            Como a <span className="text-accent">IA</span> viabiliza sua permuta
          </h2>
          <p className="text-muted-foreground font-body text-lg max-w-lg mx-auto">
            Nosso algoritmo inteligente conecta imóveis e profissionais automaticamente — sem taxa de cadastro, com menos burocracia
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="relative text-center"
            >
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-px border-t-2 border-dashed border-border" />
              )}
              <div className="w-20 h-20 rounded-2xl gradient-hero flex items-center justify-center mx-auto mb-5 shadow-lg">
                <step.icon className="text-primary-foreground" size={32} />
              </div>
              <span className="inline-block text-xs font-display font-bold text-accent mb-2">
                Passo {i + 1}
              </span>
              <h3 className="text-xl font-display font-bold text-foreground mb-2">
                {step.title}
              </h3>
              <p className="text-muted-foreground font-body text-sm leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="max-w-3xl mx-auto bg-accent/5 border border-accent/15 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={20} className="text-accent" />
              <h3 className="text-lg font-display font-bold text-foreground">
                Precisa de apoio durante a permuta?
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Você pode contratar serviços profissionais diretamente pela plataforma, a qualquer momento do processo:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                {
                  icon: Search,
                  title: "Avaliação de imóvel",
                  desc: "Avaliação profissional do seu imóvel ou do imóvel da outra parte.",
                },
                {
                  icon: Shield,
                  title: "Análise documental",
                  desc: "Checagem completa da matrícula e documentos do imóvel.",
                },
                {
                  icon: Handshake,
                  title: "Contrato de permuta",
                  desc: "Geração automática do contrato com validade jurídica.",
                },
                {
                  icon: Building2,
                  title: "Acompanhamento cartorial",
                  desc: "Suporte completo até a conclusão no cartório.",
                },
              ].map((service) => (
                <Link
                  to="/pacote-juridico"
                  key={service.title}
                  className="flex items-start gap-3 bg-background/60 rounded-xl p-3 border border-border/50 hover:border-accent/40 hover:bg-accent/5 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                    <service.icon size={16} className="text-accent" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground group-hover:text-accent transition-colors">{service.title}</p>
                    <p className="text-xs text-muted-foreground">{service.desc}</p>
                  </div>
                  <ArrowRight size={14} className="text-muted-foreground group-hover:text-accent transition-colors mt-1 shrink-0 opacity-0 group-hover:opacity-100" />
                </Link>
              ))}
            </div>

            {/* Venda pós-permuta */}
            <Link to="/pacote-juridico" className="block mt-5 p-4 bg-primary/5 border border-primary/15 rounded-xl hover:border-primary/30 hover:bg-primary/10 transition-colors group">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <DollarSign size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">Venda do imóvel recebido</p>
                    <span className="text-[10px] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full">NOVO</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Muitos permutantes não querem ficar com o imóvel recebido — trocam um apartamento por casa, terreno ou sala comercial apenas como estratégia.
                    A plataforma conecta você a compradores para <span className="font-semibold text-foreground">vender o imóvel recebido na permuta</span> de forma rápida e segura.
                  </p>
                  <div className="flex items-center gap-4 mt-2">
                    {[
                      { from: "Apartamento", to: "Casa" },
                      { from: "Casa", to: "Terreno" },
                      { from: "Apto", to: "Sala comercial" },
                    ].map((ex) => (
                      <span key={ex.to} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                        {ex.from} <ArrowRightLeft size={10} className="text-primary" /> {ex.to}
                      </span>
                    ))}
                  </div>
                </div>
                <ArrowRight size={16} className="text-muted-foreground group-hover:text-primary transition-colors mt-1 shrink-0 opacity-0 group-hover:opacity-100" />
              </div>
            </Link>

            <Link to="/pacote-juridico" className="block mt-4 text-center group">
              <p className="text-xs text-muted-foreground">
                Disponível como{" "}
                <span className="font-semibold text-accent group-hover:underline">pacote jurídico digital</span>{" "}
                — contrate apenas o que precisar →
              </p>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
